import { SHAPE, grossFromMeasurements, dimensionsFromCapacity } from "./capacity";

// Optional "help me work out the tank size" workflow. It only wraps the
// existing capacity math: dimensions -> gallons, or gallons + one known
// dimension -> estimated remaining dimensions. Nothing here is saved until
// the installer explicitly accepts the result.

export function emptyTankSizing() {
  return {
    shape: SHAPE.RECTANGLE,
    method: "measurements",
    dims: { length_in: "", width_in: "", depth_in: "", diameter_in: "" },
    capacity_gallons: "",
    ratio: "",
  };
}

export function resolveTankSizing(input) {
  if (!Object.values(SHAPE).includes(input.shape)) {
    return { errors: { shape: "Choose a supported tank shape." } };
  }
  if (!["measurements", "rated", "estimate"].includes(input.method)) {
    return { errors: { method: "Choose what information you have." } };
  }
  const known = input.method !== "measurements";
  const gallons = Number(input.capacity_gallons);
  if (known && (!Number.isFinite(gallons) || gallons <= 0)) {
    return { errors: { capacity_gallons: "Enter a positive full capacity in US gallons." } };
  }
  let dims = { ...input.dims };
  if (input.method === "estimate") {
    const inverse = dimensionsFromCapacity(input.shape, {
      capacity_gallons: gallons,
      depth_in: input.dims.depth_in,
      diameter_in: input.dims.diameter_in,
      ratio: input.ratio,
    });
    if (Object.keys(inverse.errors).length) return { errors: inverse.errors };
    dims = inverse.dims;
  }
  const forward = grossFromMeasurements(input.shape, dims);
  if (Object.keys(forward.errors).length) return { errors: forward.errors };
  if (!Number.isFinite(forward.gallons) || forward.gallons <= 0) {
    return { errors: { capacity_gallons: "Dimensions are outside the supported calculation range." } };
  }
  if (input.shape === SHAPE.SQUARE) dims.width_in = dims.length_in;
  // Keep only dimensions meaningful for this shape; do not save hidden stale inputs.
  const keys = input.shape === SHAPE.RECTANGLE || input.shape === SHAPE.SQUARE
    ? ["length_in", "width_in", "depth_in"]
    : input.shape === SHAPE.CYLINDER_VERTICAL
      ? ["diameter_in", "depth_in"]
      : ["diameter_in", "length_in"];
  dims = Object.fromEntries(keys.map((key) => [key, Number(dims[key])]));
  if (Object.values(dims).some((value) => !Number.isFinite(value) || value <= 0)) {
    return { errors: { dims: "All resolved dimensions must be finite and positive." } };
  }
  return {
    errors: {},
    geometry: {
      shape: input.shape,
      values: { ...dims, capacity_gallons: known ? gallons : forward.gallons },
      dimensionSource: input.method === "estimate" ? "estimated" : "entered",
      capacitySource: known ? "entered" : "calculated",
      assumedEqualSides: input.method === "estimate" &&
        input.shape === SHAPE.RECTANGLE && String(input.ratio ?? "").trim() === "",
    },
    geometricGallons: forward.gallons,
  };
}
